import { Trophy, ChartNoAxesColumnIncreasing } from 'lucide-react';
import { PlayerPage, LeaguePicker } from './PlayerPage';
import { Button, LINK_CLASS } from '@league/ui';
import { useQuery } from '@tanstack/react-query';
import { useSearchParams } from 'react-router-dom';
import type { PlayerTodayResponse } from '@league/shared/contract';
import { apiFetch } from '../../lib/api';
import { standingLeaders } from './standing-leaders';
import { standingsHowTo } from '../../content/how-to';
import { useHowTo } from '../../components/HowTo';

export function PlayerStandingsPage() {
  const [params, setParams] = useSearchParams();
  const leagueId = params.get('leagueId');
  const setLeagueId = (id: string) => setParams({ leagueId: id });
  const data = useQuery({
    queryKey: ['my-standings', leagueId],
    queryFn: () =>
      apiFetch<PlayerTodayResponse>(
        `/api/my/today${leagueId ? `?leagueId=${encodeURIComponent(leagueId)}` : ''}`,
      ),
  });
  /*
    THE SAME INFO ICON AND POPUP AS THE DIRECTOR'S STANDINGS TAB (owner, 2026-10-12), from the same
    sentences. Called before the early returns, as hooks must be.
  */
  const approved = data.data?.approvedStandings;
  const ties = standingsHowTo(approved?.scoringMode ?? undefined);
  const howTo = useHowTo(
    approved?.scoringMode && !approved.tiebreakEvidenceComplete
      ? {
          ...ties,
          lines: [
            ...ties.lines,
            'Some historical tiebreak evidence is unavailable. The recorded standings cannot verify every comparison.',
          ],
        }
      : ties,
    'How ties are decided',
  );
  if (data.isPending)
    return (
      <PlayerPage>
        <p className="text-sm text-ink-3">Loading…</p>
      </PlayerPage>
    );
  if (data.isError)
    return (
      <PlayerPage>
        <p role="alert" className="player-card">
          Could not load your standings.{' '}
          <Button variant="secondary" onClick={() => void data.refetch()}>
            Try again
          </Button>
        </p>
      </PlayerPage>
    );
  const leagues = data.data.leagues;
  if (!leagues.length)
    return (
      <PlayerPage>
        <header className="player-page-header">
          <p className="player-eyebrow">Standings</p>
          <h1>Your league story</h1>
        </header>
        <p className="player-card player-empty">No leagues are available yet.</p>
      </PlayerPage>
    );
  const current = leagueId ?? data.data.today?.leagueId ?? leagues[0]?.leagueId;
  const league = leagues.find((l) => l.leagueId === current);
  const standings = data.data.approvedStandings;
  const rows = standings?.rows ?? [];
  const leaders = standingLeaders(rows);
  const me = rows.find((row) => row.isYou);
  const hasResults = Boolean(me && me.sessionsPlayed > 0);
  return (
    <PlayerPage className="player-standings-page">
      <header className="player-page-header">
        <p className="player-eyebrow">Standings</p>
        <h1>{league?.name ?? 'Your league'} standings</h1>
        <p className="player-page-intro">
          {standings?.final
            ? 'The final results, and a league worth celebrating.'
            : 'A little friendly competition, one session at a time.'}
        </p>
      </header>
      {leagues.length > 1 ? (
        <LeaguePicker leagues={leagues} value={current} onChange={setLeagueId} />
      ) : null}
      {!standings ? (
        <p className="player-card player-empty">Standings are not available yet.</p>
      ) : (
        <div className="player-standings-grid">
          <section className="player-feature" aria-label="Your standing">
            <div className="player-section-title">
              <ChartNoAxesColumnIncreasing aria-hidden="true" />
              <h2>{standings.final ? 'Where you finished' : 'Your position'}</h2>
            </div>
            {hasResults && me ? (
              <>
                <p className="player-position">
                  {me.tied ? `Tied for ${ordinal(me.rank)}` : `${ordinal(me.rank)} in your league`}
                </p>
                <p className="player-position-points">
                  {me.points} league point{me.points === 1 ? '' : 's'}
                </p>
              </>
            ) : (
              <p className="mt-2 text-[15px]">No results yet</p>
            )}
            {data.data.participation ? (
              <p className="player-result-progress">
                You’ve played {data.data.participation.sessionsPlayed} of{' '}
                {data.data.participation.sessionsTotal}{' '}
                {data.data.participation.sessionsTotal === 1 ? 'session' : 'sessions'}.
              </p>
            ) : null}
            {data.data.participation?.resultsPending ? (
              <p className="mt-2 text-sm text-on-deep-2">Results pending</p>
            ) : null}
          </section>
          <section aria-label="League leaders" className="player-card player-leaders">
            <div className="player-section-title">
              <div>
                <Trophy size={23} strokeWidth={1.5} aria-hidden="true" />
                <h2>{standings.final ? 'Final league leaders' : 'League leaders'}</h2>
              </div>
              {howTo.button}
            </div>
            {howTo.panel}
            {/*
            THE DIRECTOR'S STANDINGS CARD, so the two tables read as one product (owner, 2026-10-12):
            white card, light border, rounded corners, 60px rows with light dividers, small uppercase
            headings. Her own row keeps the screen's one tint.
          */}
            {leaders.length ? (
              <div className="overflow-clip rounded-[14px] border border-light bg-paper">
                <table className="w-full table-fixed border-collapse text-left">
                  <thead>
                    <tr className="h-12 border-b border-light">
                      {/* The director's columns, in her order (owner, 2026-10-12). */}
                      <Th className="w-14 pl-4 sm:w-20">Place</Th>
                      <Th className="px-3">Player</Th>
                      <Th className="w-[4.5rem] px-2 text-right sm:w-32 sm:px-3">Total points</Th>
                      <Th className="w-[4.5rem] pl-2 pr-4 text-right sm:w-32 sm:pl-3">Hands won</Th>
                    </tr>
                  </thead>
                  <tbody>
                    {leaders.map((row) => (
                      <tr
                        key={row.playerId}
                        className={`h-[60px] border-b border-light last:border-b-0 ${row.isYou ? 'bg-tint-signal text-on-tint-signal' : ''}`}
                      >
                        <td className={`tabular pl-4 text-[15px] ${row.isYou ? '' : 'text-ink-2'}`}>
                          {row.rank}
                        </td>
                        <th
                          scope="row"
                          className="break-words px-3 text-left text-[16px] font-semibold [overflow-wrap:anywhere]"
                        >
                          {row.isYou ? 'You' : row.name}
                        </th>
                        <td className="tabular px-2 text-right text-[16px] font-semibold sm:px-3">
                          {row.points}
                        </td>
                        <td
                          className={`tabular pl-2 pr-4 text-right text-[15px] sm:pl-3 ${row.isYou ? '' : 'text-ink-2'}`}
                        >
                          {row.handsWonVerified ? (
                            row.handsWon
                          ) : (
                            <span title="Historical win evidence is unavailable">Not recorded</span>
                          )}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            ) : (
              <p className="text-[14px] text-ink-2">No leaders yet.</p>
            )}
          </section>
          {standings.publicUrl ? (
            <a
              className={`text-[14px] ${LINK_CLASS}`}
              href={standings.publicUrl}
              target="_blank"
              rel="noreferrer"
            >
              Open the public standings page
            </a>
          ) : null}
        </div>
      )}
    </PlayerPage>
  );
}
/** The roster's and the director's standings headings — small, uppercase, quiet. */
function Th({ children, className }: { children: React.ReactNode; className: string }) {
  return (
    <th
      scope="col"
      className={`text-[11px] font-semibold uppercase tracking-[0.09em] text-ink-3 ${className}`}
    >
      {children}
    </th>
  );
}

function ordinal(n: number) {
  const tens = n % 100;
  return `${n}${tens >= 11 && tens <= 13 ? 'th' : (({ 1: 'st', 2: 'nd', 3: 'rd' } as Record<number, string>)[n % 10] ?? 'th')}`;
}
